import { isItem } from '@/state/order'
import { ANIMATION_VARIANTS } from '@/utils/constants'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import React from 'react'
import { useIsMobile } from '@/hooks/useIsMobile'

export interface ErrorButton {
  name: string
  action: () => void
}

function ErrorModal({
  isOpen,
  close,
  title,
  description,
  entities,
  primaryButton,
  secondaryButtons,
  entityObjects,
  closeDisabled = false,
}: ErrorModalProps) {
  const isMobile = useIsMobile()
  const renderEntities = () => {
    if (!entities || entities.length < 1) return null
    const displayEntities = entities.map((entity, index) => (
      <p key={`displayEntities-${index}`} className="text-white text-center">
        {entity}
      </p>
    ))
    return (
      <div className="max-h-24 overflow-y-scroll my-4 pt-3 w-[90%] mx-auto border-t border-gray-700 flex-col justify-center items-center">
        {displayEntities}
      </div>
    )
  }

  const renderEntityObejcts = () => {
    if (!entityObjects || entityObjects.length < 1) return null
    const displayEntities = entityObjects.map((entity, index) => {
      let header = ''
      let body = ''
      let footer = ''
      if (isItem(entity)) {
        header = entity.name
        body = entity.description ?? ''
        footer = `$${entity.price.toFixed(2)}`
      }
      return (
        <div key={`displayEntities-${index}`} className="text-white mb-2 pb-1 border-b border-gray-700">
          <h5 className="font-bold">{header}</h5>
          <p className="text-gray-400 text-sm mb-2">{body}</p>
          <p className="text-sm">{footer}</p>
        </div>
      )
    })

    return (
      <div className="max-h-36 overflow-y-scroll my-4 pt-3 w-[90%] mx-auto border-t border-gray-700 flex-col justify-center items-center">
        {displayEntities}
      </div>
    )
  }

  const noSecondaryButtons = !secondaryButtons || secondaryButtons.length === 0

  const renderSecondaryButtons = () => {
    if (noSecondaryButtons) {
      return null
    }
    const displaysecondaryButtons = secondaryButtons.map((secondaryBtn, index) => (
      <button
        key={`secondaryButton-${index}`}
        className="w-full py-5 text-center bg-black border-t border-gray-700 text-gray-300"
        onClick={() => {
          close()
          secondaryBtn.action()
        }}
      >
        <span>{secondaryBtn.name}</span>
      </button>
    ))

    return displaysecondaryButtons
  }

  return isOpen ? (
    <div
      className={`bg-gray-500 fixed inset-0 z-50 bg-opacity-50 ${isMobile ? 'mx-auto max-w-[820px]' : 'flex justify-center'}`}
    >
      <motion.div
        className={`w-full absolute ${isMobile ? 'bottom-12' : 'bottom-4'} layout flex justify-center`}
        animate="animate"
        variants={ANIMATION_VARIANTS}
        initial="initial"
        exit="animate"
      >
        <div className="bg-black text-white w-11/12 rounded-2xl pt-2 relative">
          {!closeDisabled && (
            <button className="flex items-end justify-end py-1 px-2 w-full">
              <XMarkIcon className="w-7" onClick={close} />
            </button>
          )}
          <div className="flex flex-col items-center mt-5 mb-8">
            <img src="/error.png" className="w-12" />
            <h1 className="text-2xl uppercase text-center max-w-sm mt-5 leading-6">{title}</h1>
            <span className="pt-3 text-gray-400 text-center max-w-xs leading-5">{description}</span>
          </div>
          {renderEntities()}
          {renderEntityObejcts()}
          {renderSecondaryButtons()}
          <button
            className={`w-full py-5 text-center bg-primary-blue rounded-b-xl`}
            onClick={() => {
              close()
              primaryButton.action()
            }}
          >
            <span>{primaryButton.name}</span>
          </button>
        </div>
      </motion.div>
    </div>
  ) : null
}

export default ErrorModal

interface ErrorModalProps {
  isOpen: boolean
  close: () => void
  title: string
  description: string
  entities?: string[]
  primaryButton: ErrorButton
  secondaryButtons?: ErrorButton[]
  entityObjects?: any[]
  closeDisabled?: boolean
}
